import React, { useEffect, useState } from "react";
import { listAll, createDoc, updateDocById, removeDoc, listCategoriesTree } from "../../services/firestore";
import { autoTranslatePatch } from "../../services/translation";
import { uploadImageToR2 } from "../../services/r2";
import { Plus, Trash2, Edit3, X, Upload, ChevronRight, Image as ImageIcon } from "lucide-react";

const EMPTY = { name: "", slug: "", description: "", image_url: "", parent_id: null, order: 0 };

function slugify(s) {
  return (s || "").toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function flattenTree(nodes, depth = 0, out = []) {
  for (const n of nodes) {
    out.push({ ...n, _depth: depth });
    if (n.children && n.children.length) flattenTree(n.children, depth + 1, out);
  }
  return out;
}

export default function AdminCategories() {
  const [tree, setTree] = useState([]);
  const [flat, setFlat] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [uploading, setUploading] = useState(false);

  const reload = async () => {
    const t = await listCategoriesTree();
    setTree(t);
    setFlat(await listAll("categories"));
  };
  useEffect(() => { reload(); }, []);

  // Flatten with depth for the parent dropdown (allows picking any nested category as parent)
  const flatWithDepth = flattenTree(tree);
  const flatList = flatWithDepth;

  const open = (it, parent_id = null) => { setEditing(it ? { ...it } : { ...EMPTY, parent_id }); setUploadErr(""); };
  const close = () => setEditing(null);
  const onName = (v) => setEditing((s) => ({ ...s, name: v, slug: s.slug || slugify(v) }));

  const save = async () => {
    if (!editing.name) return alert("Kateqoriya adını yazın.");
    setBusy(true);
    try {
      const i18n = await autoTranslatePatch({
        name: editing.name || "",
        description: editing.description || "",
      });
      const data = { ...editing, ...i18n, slug: editing.slug || slugify(editing.name), order: parseInt(editing.order, 10) || 0 };
      if (editing.id) await updateDocById("categories", editing.id, data);
      else await createDoc("categories", data);
      await reload(); close();
    } finally { setBusy(false); }
  };

  const del = async (it) => {
    if (!window.confirm(`'${it.name}' silinsin?`)) return;
    await removeDoc("categories", it.id); reload();
  };

  const onPickImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true); setUploadErr("");
    try {
      const url = await uploadImageToR2(f, { folder: "categories" });
      setEditing((s) => ({ ...s, image_url: url }));
    } catch (ex) { setUploadErr(ex.message); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className="p-10" data-testid="admin-categories">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="metadata">TAKSONOMİYA</div>
          <h1 className="font-display text-5xl mt-1">Kateqoriyalar</h1>
        </div>
        <button onClick={() => open(null)} className="btn-primary" data-testid="add-category"><Plus size={14} /> KÖK ƏLAVƏ ET</button>
      </div>

      <div className="border border-[var(--border)]">
        {flatList.length ? flatList.map((n) => (
          <div key={n.id} className="flex items-center justify-between px-3 py-2 border-b border-[var(--border-soft)] group" data-testid={`cat-row-${n.id}`}>
            <div className="flex items-center gap-3" style={{ paddingLeft: n._depth * 24 }}>
              {n._depth > 0 && <ChevronRight size={12} className="text-[var(--text-muted)]" />}
              {n.image_url ? <img src={n.image_url} alt="" className="w-8 h-8 object-cover" /> : <div className="w-8 h-8 bg-[var(--surface)] flex items-center justify-center"><ImageIcon size={12} className="text-[var(--text-muted)]" /></div>}
              <span className="font-display text-lg">{n.name}</span>
              {n.description && <span className="metadata text-[var(--text-muted)] truncate max-w-xs">{n.description}</span>}
            </div>
            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => open(null, n.id)} className="metadata text-[var(--primary)] hover:underline" data-testid={`add-sub-${n.id}`}>+ ALT KATEQORİYA</button>
              <button onClick={() => open(n)} className="p-1 hover:text-[var(--primary)]"><Edit3 size={14} /></button>
              <button onClick={() => del(n)} className="p-1 hover:text-[var(--accent)]"><Trash2 size={14} /></button>
            </div>
          </div>
        )) : (
          <div className="p-10 text-center text-[var(--text-muted)] metadata">HƏLƏ KATEQORİYA YOXDUR</div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={close}>
          <div className="bg-[var(--bg)] border border-[var(--border)] w-full max-w-xl p-8" onClick={(e) => e.stopPropagation()} data-testid="category-modal">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-3xl">{editing.id ? "Düzəliş" : "Yeni"} Kateqoriya</h2>
              <button onClick={close}><X /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="metadata">AD <span className="text-[var(--text-muted)] normal-case">— kateqoriya adı</span></label>
                <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.name} onChange={(e) => onName(e.target.value)} data-testid="cat-name" placeholder="Məsələn: Jazz" />
              </div>
              <div>
                <label className="metadata">ANA KATEQORİYA <span className="text-[var(--text-muted)] normal-case">— alt-kateqoriya yaratmaq üçün seçin (istənilən səviyyə)</span></label>
                <select className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.parent_id || ""} onChange={(e) => setEditing({ ...editing, parent_id: e.target.value || null })} data-testid="cat-parent">
                  <option value="">— yoxdur (əsas/kök kateqoriyadır) —</option>
                  {flatWithDepth
                    .filter((c) => c.id !== editing.id)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {"— ".repeat(c._depth)}{c.name}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="metadata">AÇIQLAMA <span className="text-[var(--text-muted)] normal-case">— qısa mətn (istəyə bağlı)</span></label>
                <textarea rows={2} className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div>
                <label className="metadata">SIRA <span className="text-[var(--text-muted)] normal-case">— kiçik rəqəm əvvəldə görünür</span></label>
                <input type="number" className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
              </div>
              <div>
                <label className="metadata">ŞƏKİL</label>
                <div className="mt-1 flex items-center gap-3">
                  {editing.image_url ? <img src={editing.image_url} alt="" className="w-16 h-16 object-cover border border-[var(--border)]" /> : <div className="w-16 h-16 bg-[var(--surface)]" />}
                  <label className="btn-outline cursor-pointer">
                    <Upload size={14} /> {uploading ? "…" : "R2-yə yüklə"}
                    <input type="file" accept="image/*" hidden onChange={onPickImage} />
                  </label>
                  <input className="flex-1 bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-xs" placeholder="və ya şəkil URL-i" value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} />
                </div>
                {uploadErr && <div className="text-[var(--accent)] text-sm mt-2">{uploadErr}</div>}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button onClick={close} className="btn-outline">LƏĞV ET</button>
              <button onClick={save} disabled={busy} className="btn-primary" data-testid="cat-save">{busy ? "…" : "YADDA SAXLA"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
